import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { RRule } from 'rrule';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { SpinnerDialogComponent } from 'src/app/core/components/dialogs/spinner-dialog/spinner-dialog.component';
import { ResponsiveBaseComponent } from 'src/app/core/components/responsive-base/responsive-base.component';
import { BusinessAccountModel } from 'src/app/models/business-account-model';
import { SpinnerService } from 'src/app/services/spinner.service';
import { CampaignsComponentStore } from 'src/app/stores/components/campaigns/campaigns.component-store';
import { selectBusinessAccount, selectHasSubscriptionPlanOrder, selectUnpaidInvoice } from 'src/app/stores/global/app.selectors';

@Component({
    selector: 'app-campaigns-page',
    templateUrl: './campaigns-page.component.html',
    styleUrls: ['./campaigns-page.component.scss'],
    standalone: false
})
export class CampaignsPageComponent extends ResponsiveBaseComponent implements OnInit {
  public businessAccount$: Observable<BusinessAccountModel> = this.store.select(selectBusinessAccount);
  public unpaidInvoice$ = this.store.select(selectUnpaidInvoice);
  public viewModel$ = this.campaignsComponentStore.viewModel$;
  public showAdd: boolean;
  public currentPage: number = 0;
  public pageSize: number = 20;
  public pageSizeOptions = [this.pageSize];

  private readonly ngUnsubscribeState = new Subject<void>();
  private loadingSpinnerDialogRef: MatDialogRef<SpinnerDialogComponent>;
  private businessAccount: BusinessAccountModel;

  constructor(
    private readonly store: Store<any>,
    private readonly campaignsComponentStore: CampaignsComponentStore,
    private readonly router: Router,
    private readonly spinnerService: SpinnerService,
    responsive: BreakpointObserver,
  ) { 
    super(responsive);
  }

  public ngOnInit(): void {
    this.store.select(selectBusinessAccount).pipe(filter(result => Boolean(result))).subscribe(businessAccount => {
      const gettingStartedCompanyDetails: boolean = !businessAccount.company.name || businessAccount.company.name === '';
      const gettingStartedCompanyLogo: boolean = !businessAccount.company.logo;
      if (gettingStartedCompanyDetails || gettingStartedCompanyLogo) {
        this.router.navigate(['/getting-started']);
      }
      else {
        this.businessAccount = businessAccount;

        this.campaignsComponentStore.setInitial();
        this.showSpinner();
        this.campaignsComponentStore.getCampaigns({ companyId: businessAccount.company.id, currentPage: this.currentPage, pageSize: this.pageSize });
      }
    });

    this.store.select(selectHasSubscriptionPlanOrder).pipe(takeUntil(this.ngUnsubscribeState)).subscribe((hasSubscriptionPlanOrder) => {
      if (hasSubscriptionPlanOrder) {
        this.showAdd = true;
      }
      else {
        this.showAdd = false;
      }
    });

    this.campaignsComponentStore.loaded$.pipe(takeUntil(this.ngUnsubscribeState)).subscribe((loaded) => {
      this.hideSpinner(loaded);
    });
  }

  public ngOnDestroy() : void {
    this.campaignsComponentStore.setInitial();
    this.ngUnsubscribeState.next();
    this.ngUnsubscribeState.complete();
  }

  public onPageEvent(event?: PageEvent) : PageEvent {
    this.campaignsComponentStore.getCampaigns({ companyId: this.businessAccount.company.id, currentPage: event.pageIndex, pageSize: event.pageSize });
    return event;
  }

  public create() : void {
    this.router.navigate(['/companies/', this.businessAccount.company.id, 'stamp-cards', 'create']);
  }

  public getRecurrenceDisplayString(recurrencePattern: string): string {
    if (recurrencePattern) {
      return new RRule(RRule.parseString(recurrencePattern)).toText();
    }
    else {
      return 'Does not repeat';
    }
  }

  public getDisplayedColumns() : string[] {
    if(this.isSmall() || this.isXSmall()){
      return ['id', 'name', 'campaignStatus'];
    }
    return ['id', 'name', 'startDate', 'endDate', 'recurrencePattern', 'stampsObjective', 'campaignStatus'];
  }

  private showSpinner() {
    this.loadingSpinnerDialogRef = this.spinnerService.show();
  }

  private hideSpinner(loaded: boolean) {
    if (loaded && this.loadingSpinnerDialogRef !== null) {
      this.spinnerService.hide(this.loadingSpinnerDialogRef);
      this.loadingSpinnerDialogRef = null;
    }
  }
}
